/* =============================================================================
 * PDP FAQs (yba9h9tg) — accordion only, slot-derived
 *
 * FAQs-only sibling of f4qpdp01 (which also carries an Ask AI block). FAQ items
 * arrive inline via `entry.content.items` from a data-ingestion-owned simple
 * slot (`product.metafields.spectrum.faqs_<state>`). The snippet renders
 * server-side; this runtime adds:
 *
 *   1. <sai-yba9h9tg> custom element with applyVariant(presentation, content)
 *      so cross-experience and content-experiment swaps rebuild the accordion.
 *   2. __spectrumAi.snippet.bind(node, callback) for variant resolution AND
 *      to capture the analytics `track` handle.
 *   3. Analytics events through track (faqs:open, faqs:close,
 *      faqs:widget_impression).
 *      Catalog: packages/snippet-library/docs/faqs.analytics.md.
 *
 * No-bind fallback: if window.__spectrumAi is absent (e.g. local dev without
 * the bootstrap SDK loaded, or a theme that hasn't installed the embed), the
 * SSR'd accordion remains usable with native <details> behaviour and analytics
 * are silently skipped (track = noop).
 *
 * Trust boundary:
 *   answerHtml is centrally sanitized before persistence and is the only FAQ
 *   item field emitted as HTML. Question, category, and the derived id are
 *   assigned through DOM text/attribute APIs during variant rebuilds.
 *
 * data-faq-id derivation:
 *   The backend FaqSlotItem shape does not carry an `id`. The SSR template
 *   derives data-faq-id from `item.question | handleize`; this runtime
 *   mirrors that with handleize() so applyVariant rebuilds produce the
 *   same id. Stable across reorders and draft→live promotes; changes when
 *   the question text changes (intentional — different question = different
 *   FAQ for analytics purposes).
 * ============================================================================= */

;(() => {
  if (window.__sai_yba9h9tg_initialized__) return
  window.__sai_yba9h9tg_initialized__ = true

  const SNIPPET_ID = 'yba9h9tg'
  const TAG = 'sai-yba9h9tg'
  const FEATURE_SLUG = 'faqs'

  // Mirrors Shopify Liquid's `| handleize` — used to derive data-faq-id from
  // the question text. The SSR template uses `{{ item.question | handleize }}`;
  // applyVariant rebuilds must produce the same id so analytics dedup keys
  // match across page-load and post-rebuild.
  function handleize(s) {
    if (s == null) return ''
    return String(s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  function noopTrack() {}

  // safeTrack wraps every track call so a malformed payload or downstream
  // analytics bug never breaks the accordion.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* swallow — analytics is best-effort */
      }
    }
  }

  function buildItem(item, index) {
    if (!item) return null
    const details = document.createElement('details')
    details.className = 'sai-yba9h9tg__item'
    details.dataset.faqId = handleize(item.question)
    details.dataset.faqCategory = String(item.category == null ? '' : item.category)
    details.dataset.index = String(index)
    details.setAttribute('role', 'listitem')

    const summary = document.createElement('summary')
    summary.className = 'sai-yba9h9tg__summary'
    const question = document.createElement('span')
    question.className = 'sai-yba9h9tg__question'
    question.textContent = String(item.question == null ? '' : item.question)
    const chevron = document.createElement('span')
    chevron.className = 'sai-yba9h9tg__chevron'
    chevron.setAttribute('aria-hidden', 'true')
    chevron.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" focusable="false"><path d="M3 5L7 9L11 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    summary.append(question, chevron)

    const answer = document.createElement('div')
    answer.className = 'sai-yba9h9tg__answer'
    answer.innerHTML = item.answerHtml || ''
    details.append(summary, answer)
    return details
  }

  if (!customElements.get(TAG)) {
    class SaiFaqs extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true

        // Defaults; replaced via setAnalytics() once bindAllContainers runs.
        this._track = noopTrack

        // Per-FAQ open timestamps so :close can report dwell_ms.
        this._openedAt = new Map()

        this._initAccordion()
      }

      // Called once per page-load by bindAllContainers, before the bind
      // callback fires. Until then, all tracking is a noop.
      setAnalytics(track) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
      }

      // Event-delegated toggle listener on the accordion container. The native
      // `toggle` event does NOT bubble, so we must listen in the capture
      // phase. Delegation lets us attach the listener once at init time;
      // applyVariant's innerHTML rebuild does not need to rebind because the
      // accordion container itself is preserved.
      _initAccordion() {
        const accordion = this.querySelector('.sai-yba9h9tg__accordion')
        if (!accordion || this._toggleBound) return
        this._toggleBound = true
        accordion.addEventListener(
          'toggle',
          (e) => {
            const target = e.target
            if (!target || typeof target.closest !== 'function') return
            const details = target.closest('.sai-yba9h9tg__item')
            if (details && this.contains(details)) {
              this._onItemToggle(details)
            }
          },
          true,
        )
      }

      _onItemToggle(details) {
        // Single-open: when one answer opens, close the others. Setting
        // `.open = false` dispatches a `toggle` on each closed item, so their
        // :close analytics still flow through this same delegated handler.
        if (details.open && this.dataset.singleOpen === 'true') {
          const acc = this.querySelector('.sai-yba9h9tg__accordion')
          if (acc) {
            for (const other of acc.querySelectorAll('.sai-yba9h9tg__item[open]')) {
              if (other !== details) other.open = false
            }
          }
        }

        const faqId = details.getAttribute('data-faq-id') || ''
        const questionEl = details.querySelector('.sai-yba9h9tg__question')
        const faqQuestion = questionEl ? (questionEl.textContent || '').trim() : ''
        const faqCategory = details.getAttribute('data-faq-category') || ''
        const positionAttr = details.getAttribute('data-index')
        const position = positionAttr == null ? 0 : Number.parseInt(positionAttr, 10) || 0

        const basePayload = {
          faq_id: faqId,
          faq_question: faqQuestion,
          position,
          // The PDP product SSR'd onto data-product-id; '' when absent.
          product_id: this.getAttribute('data-product-id') || '',
        }
        if (faqCategory) basePayload.faq_category = faqCategory

        if (details.open) {
          this._openedAt.set(faqId, Date.now())
          this._track(`${FEATURE_SLUG}:open_elem_clicked`, basePayload)
        } else {
          const openedAt = this._openedAt.get(faqId)
          this._openedAt.delete(faqId)
          // dwell_ms is null when :close fires without a paired :open in this
          // page-view (defensive — should not happen in practice; would
          // indicate a missed setAnalytics race or a stale toggle replay).
          const dwell_ms = openedAt != null ? Date.now() - openedAt : null
          this._track(`${FEATURE_SLUG}:close_elem_clicked`, { ...basePayload, dwell_ms })
        }
      }

      // Variant resolution entry point. Re-renders the inner DOM from the
      // resolved presentation + content. Called by the bind callback on every
      // $spectrum:variant_resolved event.
      applyVariant(presentation, content) {
        const heading =
          presentation && typeof presentation.heading === 'string' ? presentation.heading : null
        const subheading =
          presentation && typeof presentation.subheading === 'string'
            ? presentation.subheading
            : null
        const allItems = content && Array.isArray(content.items) ? content.items : []
        // Cap to data-max-faqs (0/absent = show all) so JS rebuilds match SSR.
        const maxFaqs = Number.parseInt(this.dataset.maxFaqs || '0', 10) || 0
        const items = maxFaqs > 0 ? allItems.slice(0, maxFaqs) : allItems

        // Mirror the SSR presentation modes so a runtime variant swap that
        // changes heading accent / item style / gradient colour updates the
        // host (otherwise the initial-render values would stick).
        const accent = presentation && presentation.heading_accent === 'gradient' ? 'gradient' : 'solid'
        this.setAttribute('data-heading-accent', accent)
        const itemStyle = presentation && presentation.item_style === 'card' ? 'card' : 'divider'
        this.setAttribute('data-item-style', itemStyle)
        const gradientColor =
          presentation && typeof presentation.heading_gradient_color === 'string'
            ? presentation.heading_gradient_color.trim()
            : ''
        if (accent === 'gradient' && gradientColor) {
          this.style.setProperty('--sai-yba9h9tg-gradient-end', gradientColor)
        } else {
          this.style.removeProperty('--sai-yba9h9tg-gradient-end')
        }

        if (heading != null) {
          // Target the inner text span, not the h2 — the gradient accent lives on
          // the span, so writing textContent on the h2 would drop it.
          const h = this.querySelector('.sai-yba9h9tg__heading-text')
          if (h) h.textContent = heading
        }
        if (subheading != null) {
          let sub = this.querySelector('.sai-yba9h9tg__subheading')
          if (!sub) {
            sub = document.createElement('p')
            sub.className = 'sai-yba9h9tg__subheading'
            const header = this.querySelector('.sai-yba9h9tg__header')
            if (header) header.appendChild(sub)
          }
          sub.textContent = subheading
          sub.hidden = !subheading
        }

        let accordion = this.querySelector('.sai-yba9h9tg__accordion')
        if (!accordion && items.length > 0) {
          accordion = document.createElement('div')
          accordion.className = 'sai-yba9h9tg__accordion'
          accordion.setAttribute('role', 'list')
          const container = this.querySelector('.sai-yba9h9tg__container')
          if (container) container.appendChild(accordion)
          this._toggleBound = false
          this._initAccordion()
        }
        if (!accordion) return
        accordion.replaceChildren(...items.map((item, i) => buildItem(item, i)).filter(Boolean))

        // Reset dwell tracker — replaced DOM means previous open timestamps
        // would be misleading on a future :close.
        this._openedAt = new Map()

        // No rebind needed — the toggle listener is delegated in capture
        // phase on the accordion container, which is preserved across
        // applyVariant rebuilds.
      }
    }
    customElements.define(TAG, SaiFaqs)
  }

  // ── Test surface — pure helpers exposed for unit tests when the harness
  // flag is set. Production never sets the flag.
  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiYba9h9tg = {
      buildItem,
      safeTrack,
    }
  }

  // One-shot exposure signal per page-load: fires the first time the widget
  // container reaches ≥50% viewport visibility, then disconnects. The
  // `_impression` suffix is load-bearing — the storefront SDK pattern-matches
  // it to gate the event behind the per-brand impressions kill-switch
  // (default off). IntersectionObserver is feature-detected for legacy
  // browsers; without it the event is silently skipped.
  function observeWidgetImpression(node, track, payload) {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            obs.unobserve(entry.target)
            obs.disconnect()
            track(`${FEATURE_SLUG}:widget_impression`, payload)
            return
          }
        }
      },
      { threshold: [0.5] },
    )
    observer.observe(node)
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) {
      // No-bind fallback: SSR'd accordion behaves as already rendered. The
      // custom element initialises with noopTrack via connectedCallback so
      // toggles silently skip analytics. Nothing more to do here.
      return
    }

    for (const node of containers) {
      const handles = api.bind(node, ({ variant, entry }) => {
        if (!variant?.content || !entry?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content, entry.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
      if (handles?.track) {
        observeWidgetImpression(node, safeTrack(handles.track), {
          product_id: (root && root.getAttribute('data-product-id')) || '',
        })
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
